import * as React from "react";
import { useMemo } from "react";
import { useTranslations } from "../i18n/useTranslations";
import { useUser } from "../user/useUser";
import { hrefToDashes } from "../utils";
import { IMenuItem } from "./IMenu";

type Props = {
  shouldBeDisplayed: boolean;
  className: string;
  item: IMenuItem;
};

export function ListItemWithLinkOnly({ className, item, shouldBeDisplayed }: Props) {
  const { t } = useTranslations();
  const { premium, user } = useUser();

  const displayPremium = useMemo(() => item.onlyPremium && !premium, [item.onlyPremium, premium]);
  const isDisabled = useMemo(() => item.disabled?.(user) ?? false, [item, user]);
  const maybePremium = displayPremium ? withPremiumWrapper(t("Premium"), item.label) : item.label;

  return (
    <li className={`${className} ${shouldBeDisplayed ? "elm-block" : "elm-hidden"}`}>
      {isDisabled
        ? withDisabledWrapper(shouldBeDisplayed, maybePremium)
        : withEnabledWrapper(shouldBeDisplayed, maybePremium, item)}
    </li>
  );
}

const withDisabledWrapper = (shouldBeDisplayed: boolean, children: React.ReactNode) => (
  <div
    className={`elm-flex elm-justify-between elm-items-center elm-text-gray-300 elm-text-lg elm-no-underline elm-py-3.5 elm-px-6 ${
      shouldBeDisplayed ? "elm-block" : "elm-hidden"
    }`}
  >
    {children}
  </div>
);

const withEnabledWrapper = (
  shouldBeDisplayed: boolean,
  children: React.ReactNode,
  item: IMenuItem
) => (
  <a
    tabIndex={0}
    data-track-label={`link:${hrefToDashes(item.href)}`}
    className={`focus:elm-outline-none elm-flex elm-justify-between elm-items-center elm-px-6 elm-text-dd-gray-900 elm-cursor-pointer elm-pointer-events-auto elm-text-lg elm-transition elm-no-underline elm-py-3.5 lg:hover:elm-text-dd-blue-800 ${
      shouldBeDisplayed ? "elm-block" : "elm-hidden"
    }`}
    href={item.href}
    title={item.label}
    target={item.target ?? "_parent"}
  >
    {children}
  </a>
);

const withPremiumWrapper = (premiumLabel: string, children: React.ReactNode) => (
  <>
    <span className="elm-no-underline elm-block">{children}</span>
    <span className="elm-text-xs elm-bg-dd-gold-900 elm-text-dd-gray-900 elm-px-2 elm-py-1 elm-no-underline elm-rounded-full">
      {premiumLabel}
    </span>
  </>
);
